<script lang="ts">
	import { page } from '$app/state'
	import { ui } from '#library/stores.ts'
	import { onMount } from 'svelte'
	import { fly, slide } from 'svelte/transition'
	import Image from '#components/image.svelte'
	import { format_date } from 'kitto'
	import { MediaQuery } from 'svelte/reactivity'
	import { scaleFadeFromTopRight } from '#library/transitions.ts'
	import { attrs } from '#library/attrs.ts'

	let { pattern } = $props()
	// prettier-ignore
	let { layout: { main_menu, shortcuts }, news } = $derived(page.data)

	let active = $state(-1)
	let delay = $derived(active > -1 ? 300 : 0)
	let { current } = $derived(new MediaQuery('min-width: 640px'))

	onMount(() => {
		document.body.style.overflow = 'hidden'
		return () => (document.body.style.overflow = 'auto')
	})
</script>

<div class="menu" style:--pattern={pattern} transition:scaleFadeFromTopRight>
	<div class="head">
		{#if current || active < 0}
			<button class="close" aria-label="Close" onclick={() => ($ui.menu = false)}>
				<svg class="svg">
					<use xlink:href="/images/vector.svg#close"></use>
				</svg>
			</button>
		{:else if active > -1}
			<button class="back" aria-label="Back" onclick={() => (active = -1)}>
				<svg class="svg">
					<use xlink:href="/images/vector.svg#arrow-left"></use>
				</svg>
			</button>
		{/if}
		<button class="search" aria-label="Search" onclick={() => (($ui.search = true), ($ui.menu = false))}>
			<svg class="svg">
				<use xlink:href="/images/vector.svg#search"></use>
			</svg>
			<span class="label">Search</span>
		</button>
	</div>
	<div class="main">
		<svg class="logo">
			<use xlink:href="/images/vector.svg#icon"></use>
		</svg>
		<div class="navs">
			<nav class="nav">
				{#each main_menu || [] as item, i (item._uid)}
					{@const { items, link } = item}
					<div class="item">
						<svelte:element
							this={items?.length ? 'button' : 'a'}
							class="link"
							href={link.url}
							{...attrs(item)}
							onclick={() => (items?.length ? (active = i) : ($ui.menu = false))}
							role={items?.length ? 'button' : 'link'}
						>
							{link.text}
							<svg class="arrow">
								<use xlink:href="/images/vector.svg#arrow-up-right"></use>
							</svg>
						</svelte:element>
					</div>
				{/each}
			</nav>
			<div class="subnavs">
				{#each main_menu || [] as item, i (item._uid)}
					{@const { items, link } = item}
					{#if items?.length && active === i}
						<nav class="subnav" in:fly={{ x: 10, delay }} out:fly={{ x: -10 }}>
							<a class="sublink" href={link.url} {...attrs(item)} onclick={() => ($ui.menu = false)}>
								{link.text}
							</a>
							<div class="dash"></div>
							{#each items as subitem (subitem._uid)}
								<a
									class="sublink"
									href={subitem.link.url}
									{...attrs(subitem)}
									onclick={() => ($ui.menu = false)}
								>
									{subitem.link.text}
								</a>
							{/each}
						</nav>
					{/if}
				{/each}
			</div>
		</div>
	</div>
	<aside class="aside">
		{#await news}
			Fetching news...
		{:then { data: { stories } }}
			<div class="news">
				<div class="signpost">Latest in Journal</div>
				{#each stories as item, i (i)}
					<a class="article" href="/{item.full_slug}" onclick={() => ($ui.menu = false)}>
						<Image
							src={item.content.image.filename}
							alt={item.name}
							focus={item.content.image.focus}
							aspect="16/9"
						/>
						<div class="date">{format_date('{ddd}, {DD} {MMM} {YYYY}', item.first_published_at)}</div>
						<div class="title">{item.name}</div>
					</a>
				{/each}
			</div>
		{/await}
		<button class="accord" onclick={() => ($ui.shortcuts = !$ui.shortcuts)}>
			<svg class="logo alt">
				<use xlink:href="/images/vector.svg#icon"></use>
			</svg>
			Quicklinks
			<div class="dash"></div>
		</button>
		{#if current || $ui.shortcuts}
			<nav class="shortcuts" transition:slide>
				<div class="signpost">Quicklinks</div>
				{#each shortcuts || [] as item (item._uid)}
					<a class="shortcut" href={item.link.url} {...attrs(item)} onclick={() => ($ui.menu = false)}
						>{item.link.text}</a
					>
				{/each}
			</nav>
		{/if}
	</aside>
</div>

<style lang="css">
	.menu {
		background-color: var(--c-lupton);
		color: var(--c-gimson);
		inset: 0;
		overflow-y: auto;
		position: fixed;
		overscroll-behavior: contain;
		z-index: 10;

		@media (--from-mobile) {
			display: flex;
			flex-direction: column;
		}

		@media (--from-tablet) {
			flex-direction: row;
		}

		&::after {
			background-image: var(--pattern);
			content: '';
			display: block;
			inset: auto 0 0;
			position: fixed;
			size: 1rem 100%;
			z-index: 1;
		}
	}

	.head {
		display: flex;
		gap: 1rem;
		justify-content: space-between;
		padding: 2rem;
		position: absolute;
		width: 100%;
		z-index: 1;

		@media (--from-mobile) {
			padding: 2.5rem 2rem;
		}

		@media (--from-tablet) {
			padding: 2.5rem;
		}

		@media (--from-laptop) {
			padding: 2.5rem 4rem;
		}
	}

	.main {
		align-items: end;
		display: grid;
		gap: 2rem;
		grid-template-rows: 1fr auto;
		overflow-y: auto;
		padding: 2rem;

		@media (--from-mobile) {
			padding: 8rem 2rem 2rem;
		}

		@media (--from-tablet) {
			flex: 1 0 66%;
			gap: 2.5rem;
			padding: 7rem 2.5rem;
		}

		@media (--from-laptop) {
			gap: 4rem;
			padding: 8rem 4rem;
		}
	}

	.close,
	.back {
		size: 1.5rem;

		@media (--from-mobile) {
			size: 2rem;
		}
	}

	.back {
		display: block;

		@media (--from-mobile) {
			display: none;
		}
	}

	.logo {
		display: none;
		grid-column: 1 / -1;
		margin-block-start: auto;
		size: 3.25rem;

		@media (--from-mobile) {
			display: block;

			@media (max-height: 768px) {
				display: none;
			}
		}

		&.alt {
			display: block;
			margin-inline-end: auto;
			size: 3rem;

			@media (--from-mobile) {
				display: none;
			}
		}
	}

	.navs {
		display: flex;
		gap: 2rem;

		@media (--from-laptop) {
			gap: 2.5rem;
		}
	}

	.nav {
		display: grid;
		flex: 1 0 50%;
		gap: 0.75rem;
		max-width: 30rem;

		@media (--until-mobile) {
			padding-block-start: 4rem;
		}
	}

	.link {
		align-items: center;
		display: flex;
		gap: 1rem;
		font: 400 var(--f-h3);
		justify-content: space-between;
		letter-spacing: var(--ls-h3);
		text-align: start;
		width: 100%;

		&:hover {
			color: var(--c-pink);
			.arrow {
				opacity: 1;
			}
		}
	}

	.dash {
		background-color: var(--c-gimson);
		height: 1px;
		margin-block: 1rem;
		width: 2rem;
	}

	.arrow {
		opacity: 0;
		size: 2.25rem;
	}

	.subnavs {
		flex: 1 0 50%;
		pointer-events: none;

		@media (--until-mobile) {
			inset: 0;
			padding-block-start: 4rem;
			position: absolute;
		}
	}

	.subnav {
		align-content: start;
		background-color: var(--c-lupton);
		display: grid;
		gap: 1rem;
		height: 100%;
		padding-block-start: 0.5rem;
		pointer-events: auto;
		position: absolute;
		width: 100%;

		@media (--from-mobile) {
			border-left: 1px solid var(--c-white);
			padding-inline-start: 2.5rem;
		}
	}

	.sublink {
		font: 400 var(--f-h5);
		letter-spacing: var(--ls-h5);

		&:hover {
			color: var(--c-pink);
		}
	}

	.aside {
		background-color: var(--c-badley);
		display: flex;
		flex: 0 1 auto;
		gap: 20%;
		justify-content: space-between;
		margin-block-start: auto;
		padding: 1rem;
		overflow-y: auto;

		@media (--until-mobile) {
			flex-direction: column;
			position: absolute;
			inset: auto auto 0 0;
			width: 100%;
			z-index: 1;
		}

		@media (--from-mobile) {
			padding: 2rem;
		}

		@media (--from-tablet) {
			flex: 1 1 33%;
			flex-direction: column;
			gap: 0;
			margin-block-start: 0;
			max-width: 20rem;
		}

		@media (--from-laptop) {
			max-width: 25rem;
			padding: 4rem;
		}
	}

	.search {
		align-items: center;
		display: flex;
		grid-column: 1 / -1;
		font: 400 var(--f-signpost);
		gap: 0.5rem;
		letter-spacing: var(--ls-signpost);
		text-transform: uppercase;

		@media (--from-mobile) {
			order: -1;
		}
	}

	.label {
		display: none;

		@media (--from-mobile) {
			display: block;
		}
	}

	.svg {
		size: 2.5rem;
	}

	.signpost {
		display: none;

		@media (--from-mobile) {
			border-bottom: 1px solid var(--c-gimson);
			display: block;
			flex: 0 0 100%;
			font: 500 var(--f-signpost);
			letter-spacing: var(--ls-signpost);
			margin-block-end: 1rem;
			pointer-events: none;
			text-transform: uppercase;
		}
	}

	.news {
		display: none;
		flex: 0 0 33%;
		margin-block: 0 auto;

		@media (--from-mobile) {
			display: grid;
			order: 1;
		}

		@media (--from-tablet) {
			margin-block: 3rem auto;
			order: 0;
		}
	}

	.article {
		overflow: hidden;

		&:nth-child(3) {
			display: none;

			@media (--from-tablet) {
				display: block;

				@media (max-height: 768px) {
					display: none;
				}
			}
		}
	}

	.date {
		font: 400 var(--f-signpost);
		letter-spacing: var(--ls-signpost);
		margin-block-start: 0.75rem;
		opacity: 0.6;
	}

	.title {
		font: 500 var(--f-body2);
		letter-spacing: var(--ls-body2);
		overflow: hidden;
		-ms-overflow-style: none;
		scrollbar-width: none;
		text-overflow: ellipsis;
		white-space: nowrap;
		word-wrap: break-word;

		@media (--from-tablet) {
			margin-block-end: 1.5rem;
		}

		&::-webkit-scrollbar {
			display: none;
		}
	}

	.shortcuts {
		align-content: start;
		display: grid;
		flex-direction: column;
		gap: 0.75rem;
		height: calc(100vh - 12rem);
		padding-block: 2rem 0;

		@media (--from-mobile) {
			display: flex;
			flex-direction: row;
			flex-wrap: wrap;
			height: auto;
			padding-block: 0;
		}

		@media (--from-tablet) {
			display: grid;
			flex-direction: column;
			padding-block: 2rem 0;
		}
	}

	.shortcut {
		flex: 0 0 calc(50% - 0.5rem);
		font: 500 var(--f-h2);
		letter-spacing: var(--ls-h2);
		text-transform: uppercase;
		text-align: end;

		@media (--from-mobile) {
			font: 400 var(--f-body2);
			letter-spacing: var(--ls-body2);
			text-align: start;
		}
	}

	.accord {
		display: none;

		@media (--until-mobile) {
			align-items: center;
			background-color: hsl(0 0% 0% / 0.1);
			display: flex;
			font: 400 var(--f-body1);
			gap: 0.875rem;
			letter-spacing: var(--ls-body1);
			margin: -1rem -2rem 0;
			padding: 1rem 1.75rem;
			text-transform: uppercase;
		}
	}

	.dash {
		background-color: var(--c-gimson);
		height: 1px;
		width: 1rem;
	}
</style>
